import { NetworkStatus } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useCallback, useMemo, useState } from 'react';

import { IOTSProvider } from '@src/types/oneTouchSwitching';
import { getOtsDirectory } from '@utils/queries/otsQuery';

type OTSProvidersResult = {
  providers: IOTSProvider[];
  isLoading: boolean;
  foundProviders: boolean;
  hasErrored: boolean;
  refetch: () => void;
  retryIfNeeded: () => void;
};

export const useOTSProviders = (): OTSProvidersResult => {
  const { data, loading, error, refetch, networkStatus } =
    useQuery(getOtsDirectory);
  const [retryIfNeededInvoked, setRetryIfNeededInvoked] = useState(false);

  const providers = useMemo((): IOTSProvider[] => {
    return (
      data?.fixed_line_ots_directory?.list
        ?.find((list) => list?.listType === 'RCPID') // 'RCPID' = 'Retail Communications Provider ID'
        ?.identity?.filter((p) => !!p)
        .sort((a, b) => a.name.localeCompare(b.name)) ?? []
    );
  }, [data]);

  /*
   * This function can be used to retry the query if needed, it can only be invoked once
   * This is useful for retrying if you click the provider dropdown open for the first time
   */
  const retryIfNeeded = useCallback(() => {
    if (retryIfNeededInvoked) return;

    if (error || providers?.length === 0) {
      refetch();
      setRetryIfNeededInvoked(true);
    }
  }, [error, providers, refetch, retryIfNeededInvoked]);

  if (!data || loading || error) {
    return {
      providers: [],
      isLoading: loading || networkStatus === NetworkStatus.refetch,
      foundProviders: false,
      hasErrored: !!error,
      retryIfNeeded,
      refetch,
    };
  }

  return {
    providers,
    hasErrored: !!error,
    refetch,
    retryIfNeeded,
    isLoading: loading || networkStatus === NetworkStatus.refetch,
    foundProviders: !!providers && providers?.length > 0,
  };
};
